import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux';
import { useVendorAuth } from '../context/VendorAuthContext';
import { fetchBusinessesByVendor } from '../state/slices/businessSlice';
import ImageCropUploader from './ImageCropUploader';
import { checkFieldsForProfanity } from '../helper/profanityFilter';

const TITLE_PLACEHOLDER_GROUPS = [
    ["Free Dessert w/ entrée", "BOGO Appetizer", "BOGO Drink", "Free Side Dish"],
    ["Free Coffee w/ Breakfast", "2-for-1", "Free Add-On", "Free App w/Entree"],
    ["Free Appetizer", "Free Drink", "Free Upgrade", "Free Pastry w/ Coffee"],
    ["Group Discount", "Couple's Special", "Free Desert", "Free Item w/Purchase"],
];

const RotatingPlaceholder = ({ value, onChange, name, className, maxLength }) => {
    const [groupIndex, setGroupIndex] = useState(0);
    const [opacity, setOpacity] = useState(1);

    useEffect(() => {
        const DISPLAY_MS = 3000;
        const FADE_MS = 400;
        const interval = setInterval(() => {
            setOpacity(0);
            setTimeout(() => {
                setGroupIndex(i => (i + 1) % TITLE_PLACEHOLDER_GROUPS.length);
                setOpacity(1);
            }, FADE_MS);
        }, DISPLAY_MS + FADE_MS);
        return () => clearInterval(interval);
    }, []);

    const placeholderText = 'Example: ' + TITLE_PLACEHOLDER_GROUPS[groupIndex].join(' / ');

    return (
        <div style={{ position: 'relative' }}>
            <input
                type="text"
                className={className}
                name={name}
                value={value}
                onChange={onChange}
                placeholder=""
                maxLength={maxLength}
                style={{ background: 'transparent', position: 'relative', zIndex: 1 }}
            />
            {!value && (
                <span
                    style={{
                        position: 'absolute',
                        left: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: '#94a3b8',
                        fontSize: 14,
                        opacity,
                        transition: `opacity 0.4s ease`,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        maxWidth: 'calc(100% - 32px)',
                        zIndex: 0,
                    }}
                >
                    {placeholderText}
                </span>
            )}
        </div>
    );
};

const CouponRegistration = () => {
    const { vendor } = useVendorAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const businesses = useSelector((state) => state.businesses.vendorBusinesses);
    const CATEGORY_OPTIONS = [
        "Attractions",
        "Beer / Drinks",
        "Accommodations",
        "Coffee Shops",
        "Dining",
        "Retail",
        "Transportation",
        "Golf / Mini",
        "Tours / Excursions",
        "Spa / Health",
        "Museums",
        "Misc",
    ];

    const [couponData, setCouponData] = useState({
        vendor_id: vendor?.id || "",
        business_id: "",
        title: "",
        description: "",
        discount_category: "",
        coupon_type: "Standard",
        saving_percentage: "",
        dollar_savings_amount: "",
        max_redemptions_per_user: 1,
        front_page_coupon_type: false,
        starts_at: "",
        expires_at: "",
        active: true,
        image_url: "",
        image_url_2: "",
        image_url_3: "",
    });

    useEffect(() => {
        if (!vendor?.id) return;
        setCouponData((prev) => ({ ...prev, vendor_id: vendor.id }));
        dispatch(fetchBusinessesByVendor(vendor.id));
    }, [vendor, dispatch]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const isFormComplete = Boolean(
        couponData.business_id &&
        couponData.title &&
        couponData.description &&
        couponData.discount_category &&
        couponData.saving_percentage &&
        couponData.dollar_savings_amount &&
        couponData.starts_at &&
        couponData.expires_at
    );

    const selectedBusiness = businesses.find(b => b.id === parseInt(couponData.business_id)) || null;
    const uploadedPreviewImages = [couponData.image_url, couponData.image_url_2, couponData.image_url_3].filter(Boolean);
    const previewImages = uploadedPreviewImages.length > 0 ? uploadedPreviewImages : [
        "assets/images/thumbs/product-details-two-thumb1.png",
        "assets/images/thumbs/product-details-two-thumb2.png",
        "assets/images/thumbs/product-details-two-thumb3.png",
    ];
    const previewSpecs = [
        ["Coupon Type", `${couponData.coupon_type} based`],
        ["Coupon Name", couponData.title],
        ["Discount Category", couponData.discount_category],
        ["Savings Percentage", couponData.saving_percentage ? `${couponData.saving_percentage}%` : "—"],
        ["Dollar Savings Amount", couponData.dollar_savings_amount ? `$${couponData.dollar_savings_amount}` : "—"],
        ["Max Redemptions Per Subscription", String(couponData.max_redemptions_per_user)],
    ];

    const sectionRef = useRef(null);
    const previewColRef = useRef(null);
    const innerRef = useRef(null);

    useEffect(() => {
        const FIXED_TOP = 120;    // px from viewport top where card pins when stuck
        const STICK_AFTER = 350;  // additional px to scroll before card becomes sticky
        const update = () => {
            const section = sectionRef.current;
            const col = previewColRef.current;
            const inner = innerRef.current;
            if (!section || !col || !inner) return;

            const sectionRect = section.getBoundingClientRect();
            const innerHeight = inner.offsetHeight;

            if (sectionRect.top <= FIXED_TOP - STICK_AFTER && sectionRect.bottom >= innerHeight + FIXED_TOP) {
                inner.style.position = 'fixed';
                inner.style.top = FIXED_TOP + 'px';
                inner.style.left = col.getBoundingClientRect().left + 'px';
                inner.style.width = col.offsetWidth + 'px';
            } else {
                inner.style.position = '';
                inner.style.top = '';
                inner.style.left = '';
                inner.style.width = '';
            }
        };

        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update, { passive: true });
        update();
        return () => {
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, []);

    const handleChange = (e) => {
        setCouponData({
            ...couponData,
            [e.target.name]: e.target.value,
        });
    };

    const handleCouponRegister = async (e) => {
        e.preventDefault();

        const profanityError = checkFieldsForProfanity({
            "Title": couponData.title,
            "Description": couponData.description,
        });
        if (profanityError) { setError(profanityError); return; }

        try {

            setLoading(true);
            setError("");
            setSuccess("");

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/coupons/register`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        vendor_id: couponData.vendor_id,
                        business_id: couponData.business_id,

                        title: couponData.title,
                        description: couponData.description,

                        discount_category: couponData.discount_category,
                        coupon_type: "Standard",

                        saving_percentage: couponData.saving_percentage,
                        dollar_savings_amount: couponData.dollar_savings_amount,

                        max_redemptions_per_user: couponData.max_redemptions_per_user,

                        front_page_coupon_type: false,

                        starts_at: couponData.starts_at,
                        expires_at: couponData.expires_at,

                        active: couponData.active,
                        image_url: couponData.image_url || null,
                        image_url_2: couponData.image_url_2 || null,
                        image_url_3: couponData.image_url_3 || null,
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || "Vendor registration failed");
                return;
            }

            navigate("/vendor-dashboard");

            // optional form reset
            setCouponData({
                vendor_id: "",
                business_id: "",

                title: "",
                description: "",

                discount_category: "",
                coupon_type: "Standard",

                saving_percentage: "",
                dollar_savings_amount: "",

                max_redemptions_per_user: 1,

                front_page_coupon_type: false,

                starts_at: "",
                expires_at: "",

                active: true,
                image_url: "",
                image_url_2: "",
                image_url_3: "",
            });

        } catch (error) {

            console.error(error);

            setError("Server error. Please try again.");

        } finally {

            setLoading(false);

        }
    };

    return (
    <>
    <section className="vendor-registration py-80" ref={sectionRef}>
    <div className="container container-lg">

        {/* Header */}
        <div className="text-center mb-48">
            <h2 className="mb-16">Create a Coupon</h2>
            <p className="text-gray-600">
                Build and publish coupons for your businesses and vendors.
            </p>
        </div>

        <div className="row">
            <div className="col-lg-7 col-xl-8">
                <div className="border border-gray-100 rounded-16 p-40 bg-white">

                    {/* Header Info */}
                    <div className="mb-40 pb-32 border-bottom border-gray-100">
                        <h5 className="mb-8">Coupon Details</h5>
                        <p className="text-gray-500 mb-0">
                            Fill out all required coupon fields below.
                        </p>
                    </div>

                    {/* FORM */}
                    <form onSubmit={handleCouponRegister}>
                        <div className="row gy-4">

                            {/* VENDOR NAME */}
                            <div className="col-12">
                                <label className="mb-8 fw-medium">Vendor Name</label>
                                <input
                                    type="text"
                                    className="common-input border-gray-100 bg-gray-50"
                                    value={vendor?.vendor_name || ""}
                                    readOnly
                                />
                            </div>

                            {/* BUSINESS SELECT */}
                            <div className="col-12">
                                <label className="mb-8 fw-medium">Business</label>
                                <select
                                    className="common-input border-gray-100"
                                    name="business_id"
                                    value={couponData.business_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Select a Business</option>
                                    {businesses.map((b) => (
                                        <option key={b.id} value={b.id}>
                                            {b.business_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* TITLE */}
                            <div className="col-12">
                                <label className="mb-8 fw-medium">Coupon Title</label>
                                <RotatingPlaceholder
                                    className="common-input border-gray-100"
                                    name="title"
                                    value={couponData.title}
                                    onChange={handleChange}
                                    maxLength={35}
                                />
                                <p className="mb-0 mt-4" style={{ fontSize: 11.5, color: couponData.title.length >= 35 ? "#ef4444" : "#94a3b8", textAlign: "right" }}>
                                    {couponData.title.length}/35
                                </p>
                            </div>

                            {/* DESCRIPTION */}
                            <div className="col-12">
                                <label className="mb-8 fw-medium">Coupon Description</label>
                                <textarea
                                    className="common-input border-gray-100"
                                    name="description"
                                    value={couponData.description}
                                    onChange={handleChange}
                                    rows="4"
                                    placeholder="Describe the coupon..."
                                    maxLength={200}
                                />
                                <p className="mb-0 mt-4" style={{ fontSize: 11.5, color: couponData.description.length >= 200 ? "#ef4444" : "#94a3b8", textAlign: "right" }}>
                                    {couponData.description.length}/200
                                </p>
                            </div>

                            {/* COUPON PHOTOS */}
                            <div className="col-12">
                                <div className="border border-gray-100 rounded-16 p-24 bg-gray-50">
                                    <h6 className="mb-4 fw-semibold text-gray-800">Coupon Photos</h6>
                                    <p className="text-gray-400 text-sm mb-24">Add up to 3 photos. The first photo appears on coupon cards across the site. Photos 2 and 3 only appear on the coupon detail page.</p>

                                    <div className="mb-20">
                                        <label className="mb-8 fw-medium d-block text-sm">
                                            Photo 1 — Card &amp; Detail Page <span className="text-main-600 fw-normal">(primary)</span>
                                        </label>
                                        <ImageCropUploader
                                            onUpload={(url) => setCouponData(prev => ({ ...prev, image_url: url }))}
                                            currentImageUrl={couponData.image_url}
                                        />
                                    </div>

                                    <div className="mb-20">
                                        <label className="mb-8 fw-medium d-block text-sm">
                                            Photo 2 — Detail Page Only <span className="text-gray-400 fw-normal">(optional)</span>
                                        </label>
                                        <ImageCropUploader
                                            onUpload={(url) => setCouponData(prev => ({ ...prev, image_url_2: url }))}
                                            currentImageUrl={couponData.image_url_2}
                                        />
                                    </div>

                                    <div className="mb-0">
                                        <label className="mb-8 fw-medium d-block text-sm">
                                            Photo 3 — Detail Page Only <span className="text-gray-400 fw-normal">(optional)</span>
                                        </label>
                                        <ImageCropUploader
                                            onUpload={(url) => setCouponData(prev => ({ ...prev, image_url_3: url }))}
                                            currentImageUrl={couponData.image_url_3}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* COUPON TYPE — read-only */}
                            <div className="col-12">
                                <label className="mb-8 fw-medium">Coupon Type</label>
                                <input
                                    type="text"
                                    className="common-input border-gray-100 bg-gray-50"
                                    value="Standard"
                                    readOnly
                                />
                            </div>

                            {/* CATEGORY FIELDS */}
                            <div className="col-12">
                                <label className="mb-8 fw-medium">
                                    Discount Category
                                </label>

                                <select
                                    className="common-input border-gray-100"
                                    name="discount_category"
                                    value={couponData.discount_category}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Category</option>

                                    {CATEGORY_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* DISCOUNTS */}
                            <div className="col-md-6">
                                <label className="mb-8 fw-medium">Saving Percentage (%)</label>
                                <input
                                    type="number"
                                    className="common-input border-gray-100"
                                    name="saving_percentage"
                                    value={couponData.saving_percentage}
                                    onChange={handleChange}
                                    placeholder="e.g. 20"
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="mb-8 fw-medium">Dollar Savings Amount ($)</label>
                                <input
                                    type="number"
                                    className="common-input border-gray-100"
                                    name="dollar_savings_amount"
                                    value={couponData.dollar_savings_amount}
                                    onChange={handleChange}
                                    placeholder="e.g. 10"
                                />
                            </div>

                            {/* LIMITS */}
                            <div className="col-12">
                                <label className="mb-8 fw-medium">Max Redemptions Per User</label>
                                <select
                                    className="common-input border-gray-100"
                                    name="max_redemptions_per_user"
                                    value={couponData.max_redemptions_per_user}
                                    onChange={handleChange}
                                >
                                    {[...Array(10)].map((_, i) => (
                                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                                    ))}
                                </select>
                            </div>

                            {/* DATES */}
                            <div className="col-md-6">
                                <label className="mb-8 fw-medium">Start Date</label>
                                <input
                                    type="datetime-local"
                                    className="common-input border-gray-100"
                                    name="starts_at"
                                    value={couponData.starts_at}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="mb-8 fw-medium">Expiry Date</label>
                                <input
                                    type="datetime-local"
                                    className="common-input border-gray-100"
                                    name="expires_at"
                                    value={couponData.expires_at}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* ALERTS */}
                            <div className="col-12">
                                {error && <div className="alert alert-danger">{error}</div>}
                                {success && (
                                    <>
                                        <div className="alert alert-success">{success}</div>
                                        <div className="d-flex justify-content-center mt-16">
                                            <Link
                                                to="/vendor-dashboard"
                                                className="btn btn-main rounded-8 px-32 py-14"
                                            >
                                                Vendor Dashboard
                                            </Link>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* SUBMIT */}
                            <div className="col-12 mt-16 d-flex flex-wrap gap-16 align-items-center">
                                <button
                                    type="submit"
                                    className="btn bg-main-800 text-white py-18 px-40 rounded-8"
                                    disabled={loading}
                                >
                                    {loading ? "Creating Coupon..." : "Create Coupon"}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-main py-18 px-40 rounded-8"
                                    disabled={!isFormComplete}
                                    title={!isFormComplete ? "Fill out all required fields to preview" : "Preview coupon page"}
                                    onClick={() => setShowPreviewModal(true)}
                                >
                                    Coupon Page Preview
                                </button>
                            </div>

                        </div>
                    </form>

                </div>
            </div>

            {/* PREVIEW COLUMN */}
            <div className="col-lg-5 col-xl-4 d-none d-lg-block" ref={previewColRef}>
                <div ref={innerRef} style={{ maxWidth: '240px' }}>
                    <p className="text-gray-500 fw-medium mb-16 text-center">Card Preview</p>
                    <div className="product-card h-100 p-16 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2">
                        <div className="product-card__thumb flex-center rounded-8 bg-gray-50 position-relative" style={{ overflow: 'hidden', minHeight: 160 }}>
                            <span className="product-card__badge bg-tertiary-600 px-8 py-4 text-sm text-white position-absolute inset-inline-start-0 inset-block-start-0">
                                {couponData.saving_percentage ? `${couponData.saving_percentage}% Off` : 'New'}
                            </span>
                            {couponData.image_url ? (
                                <img
                                    src={couponData.image_url}
                                    alt=""
                                    style={{ width: '100%', height: '100%', maxWidth: 'none', maxHeight: 'none', objectFit: 'cover', position: 'absolute', inset: 0 }}
                                />
                            ) : (
                                <img
                                    src="assets/images/thumbs/product-two-img1.png"
                                    alt=""
                                    className="w-auto max-w-unset"
                                />
                            )}
                        </div>
                        <div className="product-card__content w-100 mt-16 d-flex flex-column h-100">
                            <span className="text-main-600 bg-main-50 text-sm fw-medium py-4 px-8">
                                {couponData.saving_percentage ? `${couponData.saving_percentage}% OFF` : '— % OFF'}
                            </span>
                            <div className="flex-align gap-6 mt-16">
                                <div className="flex-align gap-4">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <span key={star} className="text-15 fw-medium text-gray-300 d-flex">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                    ))}
                                </div>
                                <span className="text-xs fw-medium text-gray-500">—</span>
                                <span className="text-xs fw-medium text-gray-500">(0)</span>
                            </div>
                            <h6 className="title text-lg fw-semibold my-18">
                                <span className="link mb-1 d-block" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {couponData.title || <span className="text-gray-400 fw-normal">Coupon title will appear here</span>}
                                </span>
                            </h6>
                            <span className="py-2 px-8 text-xs rounded-pill text-main-two-700 bg-main-two-200 mt-16">
                                {couponData.discount_category || 'Category'}
                            </span>
                            <span className="mt-10 description-wrapper">
                                <p className="text-neutral-600 mt-4 mb-0" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {couponData.description || <span className="text-gray-400">Description will appear here</span>}
                                </p>
                            </span>
                        </div>
                        <button
                            type="button"
                            className="product-card__cart w-100 btn bg-main-800 text-white hover-bg-main-700 hover-text-white py-11 px-24 rounded-8 flex-center gap-8 fw-medium mt-16"
                            disabled
                        >
                            Redeem Coupon <i className="ph ph-shopping-cart" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>

{showPreviewModal && (
    <div
        onClick={(e) => { if (e.target === e.currentTarget) setShowPreviewModal(false); }}
        style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.65)',
            overflowY: 'auto',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '32px 16px',
        }}
    >
        <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 1200, position: 'relative' }}>

            {/* Banner */}
            <div style={{ background: '#333B94', color: 'white', padding: '12px 24px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="fw-medium">Coupon Page Preview — Not yet published</span>
                <button
                    type="button"
                    onClick={() => setShowPreviewModal(false)}
                    style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
                >
                    <i className="ph ph-x" />
                </button>
            </div>

            {/* Page content */}
            <div className="p-40">
                <div className="row gy-4 mb-48">

                    {/* Left: images */}
                    <div className="col-xl-6">
                        <div className="product-details__left">
                            <div className="product-details__thumb-slider border border-gray-100 rounded-16">
                                <div className="product-details__thumb flex-center p-32">
                                    <img src={previewImages[0]} alt="Preview" style={{ maxHeight: 320, objectFit: 'contain' }} />
                                </div>
                            </div>
                            <div className="d-flex gap-8 mt-16">
                                {previewImages.map((img, i) => (
                                    <div key={i} className="border border-gray-100 rounded-16 p-8 flex-center" style={{ width: 80, height: 80 }}>
                                        <img src={img} alt={`thumb ${i}`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right: details */}
                    <div className="col-xl-6">
                        <div className="product-details__content">
                            <h5 className="mb-12">{couponData.title}</h5>
                            <div className="flex-align flex-wrap gap-12 mb-16">
                                <div className="flex-align gap-4">
                                    {[1,2,3,4,5].map(star => (
                                        <span key={star} className="text-15 fw-medium d-flex text-gray-300">
                                            <i className="ph-fill ph-star" />
                                        </span>
                                    ))}
                                </div>
                                <span className="text-sm fw-medium text-neutral-600">No ratings yet</span>
                                {selectedBusiness && (
                                    <>
                                        <span className="text-sm fw-medium text-gray-500">|</span>
                                        <span className="text-gray-900">{selectedBusiness.business_name}</span>
                                    </>
                                )}
                            </div>
                            <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                            <p className="text-gray-700">{couponData.description}</p>
                            <div className="my-32 flex-align gap-16 flex-wrap">
                                <div className="flex-align gap-8">
                                    <div className="flex-align gap-8 text-main-two-600">
                                        <i className="ph-fill ph-seal-percent text-xl" />
                                        {couponData.saving_percentage}%
                                    </div>
                                    <h6 className="mb-0">${couponData.dollar_savings_amount}</h6>
                                </div>
                                <div className="flex-align gap-8">
                                    <span className="text-gray-700">Savings Value</span>
                                    <h6 className="text-xl text-gray-400 mb-0 fw-medium">${couponData.dollar_savings_amount}</h6>
                                </div>
                            </div>
                            <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                            <button
                                type="button"
                                disabled
                                className="btn btn-black flex-center gap-8 rounded-8 py-16"
                                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                            >
                                <i className="ph ph-ticket text-lg" />
                                Redeem Coupon
                            </button>
                            <div className="mt-32">
                                <span className="fw-medium text-gray-900">100% Safe Checkout Guaranteed</span>
                                {/* <div className="mt-10">
                                    <img src="assets/images/thumbs/gateway-img.png" alt="" />
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="product-dContent border rounded-24">
                    <div className="product-dContent__header border-bottom border-gray-100 flex-between flex-wrap gap-16">
                        <ul className="nav common-tab nav-pills mb-3" id="modal-pills-tab" role="tablist">
                            <li className="nav-item" role="presentation">
                                <button className="nav-link active" data-bs-toggle="pill" data-bs-target="#modal-pills-description" type="button" role="tab">
                                    Description
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button className="nav-link" data-bs-toggle="pill" data-bs-target="#modal-pills-reviews" type="button" role="tab">
                                    Reviews
                                </button>
                            </li>
                        </ul>
                    </div>
                    <div className="product-dContent__box">
                        <div className="tab-content" id="modal-pills-tabContent">
                            <div className="tab-pane fade show active" id="modal-pills-description" role="tabpanel">
                                <div className="mb-40">
                                    <h6 className="mb-24">Product Description</h6>
                                    <p>{couponData.description}</p>
                                </div>
                                <div className="mb-40">
                                    <h6 className="mb-24">Coupon Specifications</h6>
                                    <ul className="mt-32">
                                        {previewSpecs.map(([label, value]) => (
                                            <li key={label} className="text-gray-400 mb-14 flex-align gap-14">
                                                <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle flex-shrink-0">
                                                    <i className="ph ph-check" />
                                                </span>
                                                <span className="text-heading fw-medium">
                                                    {label}:
                                                    <div className="text-gray-500">{value || '—'}</div>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                {selectedBusiness && (
                                    <div className="mb-0">
                                        <h6 className="mb-24">{selectedBusiness.business_name} Information</h6>
                                        <ul className="mt-32">
                                            {[selectedBusiness.address, selectedBusiness.city, selectedBusiness.zipcode].filter(Boolean).map((val, i) => (
                                                <li key={i} className="text-gray-400 mb-14 flex-align gap-14">
                                                    <span className="w-20 h-20 bg-main-50 text-main-600 text-xs flex-center rounded-circle flex-shrink-0">
                                                        <i className="ph ph-check" />
                                                    </span>
                                                    <span className="text-gray-500">{val}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                            <div className="tab-pane fade" id="modal-pills-reviews" role="tabpanel">
                                <p className="text-gray-500">No reviews yet — this coupon hasn't been published.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
)}
    </>
    )
}

export default CouponRegistration